define((require) => {
    "use strict";

    const {first}        = require("underscore");
    const ViewController = require("editor/util/ViewController");
    const Publishables   = require("ui/publish/Publishables");
    const PublishUtil    = require("ui/publish/PublishUtil");
    const {isBlank}      = require("shared/util/StringUtils");
    const ImprovedModel  = require("shared/zbone/ImprovedModel");


    /**
     * Fetches publishable models (experiences/groups)
     *
     * @param {String} term           The search term used to match and filter publishables.
     * @param {ProjectModel} project  The project model.
     * @returns {Promise}             Promise holding information about publishables.
     */
    const getPublishables = (term, project) => {
        return Publishables.fetch({project, term});
    };

    /**
     * View Controller of a channel property view, handling logic to lazy load manual slot dropdowns, both Live and Scheduled.
     *
     */
    return ViewController.extend({

        _currentPublishableCollection: null,

        _areProjectSlotsLoaded: false,

        _areAllSlotsBeingLoaded: false,

        _limit: 20,

        /**
         * Initializes the dropdown values when lightbox is opened.
         *
         * @param {Function}                    callback  Select2 callback function to be used to set the data.
         * @param {ExperienceModel|GroupModel}  model     The model to be set as value to the dropdown.
         */
        onViewInitSelection(callback, model) {
            if (model) {
                let text = null;

                if (model instanceof ImprovedModel) {
                    text = model.publishableDisplayName();
                } else {
                    text = model.name;
                }
                callback({id: model, text});
            }
        },

        /**
         * Populates the dropdowns in the manual publishing lightbox implementing lazy loading.
         *
         * @param {Object} options        Select2 options contaning info such as searched term, callback function and current page.
         * @param {ProjectModel} project  The project model.
         * @param {Boolean} schedule      Whether scheduling should be taken into account or not.
         */
        onViewFetchResults(options, project, schedule) {
            let {callback, page, term} = options;

            // We want to make sure we clear previous/pending searches
            if (page === 1 && this._currentPublishableCollection) {
                this._reset();
            }

            if (page === 1) {
                if (isBlank(term)) {
                    let publishableOptions = this.options;
                    if (project && this._currentPublishableCollection) {
                        const publishableCollection = this._currentPublishableCollection;
                        const publishableDescriptors = Publishables.descriptors(publishableCollection.models());

                        this._currentPublishableCollection.options.project = project;
                        this._currentPublishableCollection.options.term = term;

                        publishableOptions = {publishableCollection, publishableDescriptors};
                    }
                    this._populateSlots(publishableOptions, options, project, schedule);
                } else {
                    // Refetch results when already searching or on scrolled pages.
                    getPublishables(term, project)
                        .then((publishables) => {
                            this._populateSlots(publishables, options, project, schedule);
                        });
                }
            } else if (!this._currentPublishableCollection.isPending() && this._currentPublishableCollection.hasMore()) {
                this._currentPublishableCollection.next()
                    .then((publishableCollection) => {
                        let results = [];

                        if (publishableCollection.length) {
                            results.push({
                                text: PublishUtil.PENDING_OPT_GROUP_RESULT,
                                children: publishableCollection.map((content) => {
                                    return {
                                        text: content.publishableDisplayName(),
                                        id: content
                                    };
                                })
                            });
                        }

                        const more = this._currentPublishableCollection && this._currentPublishableCollection.hasMore();
                        this._areProjectSlotsLoaded = project && !more;

                        // if all Project Slots have been loaded and not "All" slots have been loaded yet...
                        if (this._areProjectSlotsLoaded && !this._areAllSlotsBeingLoaded) {
                            this._populateNextSlots({
                                callback, results, more, term
                            });
                        } else {
                            this._callbackAndCleanup({callback, results, more});
                        }
                    });
            }
        },

        _populateSlots(publishables, options, project, schedule) {
            let {callback, term} = options;
            const {publishableCollection, publishableDescriptors} = publishables;

            const results = term ? [] : [PublishUtil.EMPTY_SLOT];

            if (schedule && isBlank(term)) {
                results.push(PublishUtil.EMPTY_SCHEDULED_SLOT);
            }

            if (!publishableCollection.isEmpty()) {
                results.push(this._buildResult(project, publishableDescriptors));
            }

            let more = publishableCollection && publishableCollection.hasMore();

            this._areProjectSlotsLoaded = project && !more;
            this._areAllSlotsBeingLoaded = !project;
            this._currentPublishableCollection = publishableCollection;

            // if all Project Slots have been loaded, then we need to start fetch "All"
            if (this._areProjectSlotsLoaded) {
                this._populateNextSlots({
                    callback, results, more, term
                });
            } else {
                this._callbackAndCleanup({callback, results, more});
            }
        },

        _populateNextSlots({
            callback, results, more, term
        }) {
            getPublishables(term).then((allPublishables) => {
                const {publishableCollection, publishableDescriptors} = allPublishables;

                if (!publishableCollection.isEmpty()) {
                    results.push(this._buildResult(null, publishableDescriptors));
                }

                this._areAllSlotsBeingLoaded = true;
                this._currentPublishableCollection = publishableCollection;

                more = publishableCollection.hasMore();
                this._callbackAndCleanup({callback, results, more});
            });
        },

        _reset() {
            this._currentPublishableCollection.reset(first(this.options.publishableCollection.models(), this._limit));
            this._areAllSlotsBeingLoaded = false;
        },

        _buildResult(project, publishableDescriptors) {
            return {
                text: project ? "This Project" : "All",
                children: publishableDescriptors.map((content) => {
                    return {id: content.model, text: content.name};
                })
            };
        },

        _callbackAndCleanup({callback, results, more}) {
            if (!this.view.isClosed) {
                callback({
                    results,
                    more
                });

                this.view.removeEmptyOptGroups();
            }
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/publish/channelSetup/ChannelPropertiesViewController.js